import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import styles from "./RoomVideoPlayer.module.scss";
import ArrowLeft from "../../../components/icons/chat/arrowLeft";
import ArrowRight from "../../../components/icons/chat/arrowRight";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

interface RoomVideoPlayerProps {
  trailerKey: string | null;
  backdropPath: string | null;
  muted: boolean;
  onToggleMuted: () => void;
  onNavigateUp: () => void;
  onNavigateDown: () => void;
  canNavigateUp: boolean;
  canNavigateDown: boolean;
  isActive?: boolean;
}

const RoomVideoPlayer: React.FC<RoomVideoPlayerProps> = ({
  trailerKey,
  backdropPath,
  muted,
  onToggleMuted,
  onNavigateUp,
  onNavigateDown,
  canNavigateUp,
  canNavigateDown,
 isActive = true,
}) => {
  const [player, setPlayer] = useState<any>(null);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(false);
  const controlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const url = trailerKey ? `https://www.youtube.com/watch?v=${trailerKey}` : "";

  useEffect(() => {
    setReady(false);
    setProgress(0);
    setDuration(0);
    setPlaying(true);
  }, [trailerKey]);

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleMouseLeave = () => {
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    setShowControls(false);
  };

  const togglePlay = () => {
    if (!ready) return;
    const yt = player as any;
    if (playing) {
      yt?.pauseVideo?.();
      setPlaying(false);
    } else {
      yt?.playVideo?.();
      setPlaying(true);
    }
  };

  useEffect(() => {
    if (!player) return;
    if (muted) {
      (player as any)?.mute?.();
    } else {
      (player as any)?.unMute?.();
      (player as any)?.setVolume?.(100);
    }
  }, [player, muted]);

  useEffect(() => {
    setPlaying(isActive);
  }, [isActive]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  if (!trailerKey) {
    return (
      <div className={styles.playerWrapper}>
        <div className={styles.fallback}>
          {backdropPath ? (
            <img src={`${IMG_BASE}${backdropPath}`} alt="" className={styles.fallbackImg} />
          ) : (
            <div className={styles.noTrailer}>No trailer available</div>
          )}
        </div>
        <div className={styles.navButtons}>
          <button
            className={styles.navBtn}
            onClick={onNavigateUp}
            disabled={!canNavigateUp}
            aria-label="Previous movie"
          >
            <ArrowLeft/>
          </button>
          <button
            className={styles.navBtn}
            onClick={onNavigateDown}
            disabled={!canNavigateDown}
            aria-label="Next movie"
          >
            <ArrowRight/>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.playerWrapper}
      ref={wrapperRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.playerInner}>
        <ReactPlayer
          url={url}
          playing={isActive}
          muted={muted}
          controls={false}
          width="100%"
          height="100%"
          playsinline={true}
          loop={true}
          onReady={(p: any) => {
            setPlayer(p.getInternalPlayer());
            setReady(true);
          }}
          onProgress={({ playedSeconds }: { playedSeconds: number }) =>
            setProgress(playedSeconds)
          }
          onDuration={(d: number) => setDuration(d)}
        />
      </div>

      {/* Gradient masks to hide YouTube overlays */}
      <div className={styles.ytMaskTop} />
      <div className={styles.ytMaskBottom} />

      {/* Transparent click overlay for play/pause */}
      <button
        type="button"
        tabIndex={-1}
        className={styles.videoToggle}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        }}
        aria-label={playing ? "Pause video" : "Play video"}
      />

      {/* Play button overlay */}
      {ready && !playing && (
        <div className={styles.playOverlay} onClick={togglePlay}>
          <svg width="55" height="55" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}

      {/* Progress bar */}
      {ready && (showControls || !playing) && (
        <div className={styles.controls}>
          <span className={styles.time}>{formatTime(progress)}</span>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: duration > 0 ? `${(progress / duration) * 100}%` : "0%" }}
            />
          </div>
          <button className={styles.muteBtn} onClick={onToggleMuted}>
            {muted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Navigation arrows */}
      <div className={styles.navButtons}>
        <button
          className={styles.navBtn}
          onClick={onNavigateUp}
          disabled={!canNavigateUp}
          aria-label="Previous movie"
        >
          <ArrowLeft/>
        </button>
        <button
          className={styles.navBtn}
          onClick={onNavigateDown}
          disabled={!canNavigateDown}
          aria-label="Next movie"
        >
          <ArrowRight/>
        </button>
      </div>
    </div>
  );
};

export default RoomVideoPlayer;
